/**
 * Palette vocabulary for shaded terrain — which `BARON.PAL` entries a sloped
 * ground face is allowed to be, and the walk that picks one.
 *
 * The mandate is "1990 artists with a modern GPU": relief is expressed through
 * **shading**, not texture, and every colour that reaches the framebuffer is an
 * entry the original could have drawn. So this module contains no gradients and
 * no interpolation — it contains two ramps and an integer step, exactly like
 * `three/ramp-shade.ts`'s aircraft walk.
 *
 * ## Why the ramps are not invented
 *
 * `SHADES.DAT` reports `halfWidth = 0` for every terrain colour — indices 4,
 * 67, 68, 70 and 143 included — which is the data's own statement that the
 * engine never shades terrain at runtime. There is therefore no shipped ramp to
 * transcribe for the ground the way there is for an aeroplane's fabric, and
 * the ramps below were recovered from the palette itself.
 *
 * **The recovery, and its check.** Take a terrain colour, keep every
 * `BARON.PAL` entry within a narrow cone of its own hue, and sort those by
 * luminance. Run against colours whose ramp is already known, the rule returns
 * the known ramp:
 *
 * | anchor | what the rule returns | what the game does |
 * |---|---|---|
 * | 4 `#304965` (water) | **1, 2, 3, 4, 5, 6, 7** | the whole blue run, index 4 in the middle |
 * | 68 `#343434` (road) | 64 … 72 | the three road greys 67/68/70 sit inside it |
 * | 115 `#655d38` (hill) | 112, 113, 114, 115, 116, 117 | the hills are painted 112-115 |
 *
 * That is three independent confirmations that the rule finds the runs the
 * artists actually used, so applying it to the ground colour is a recovery
 * rather than a guess. `GROUND_RELIEF_RAMP` is its output for index 143, and
 * the notable thing about that output is that **its bright half is the hill
 * ramp**: 112, 113, 114, 115, 116 and 119 all fall inside a 0.985 hue cone of
 * the measured ground green. Red Baron's landform ramp and its ground colour
 * are the same hue family, so shading new relief through this run puts it in
 * the same palette the original's own hills are painted in.
 *
 * `test/terrain-palette.test.ts` re-derives both ramps from
 * `data/baked/palettes/BARON.PAL.json` and fails if an entry here is not the
 * real byte, so these constants cannot drift from the data.
 */

/** `0xRRGGBB`, matching `flight/time-of-day.ts`'s `PaletteColour`. */
export type PaletteColour = number;

/**
 * A ramp plus the position along it that an *unlit* face takes. `centre` exists
 * because a walk has to be able to return the measured colour exactly: level
 * ground must draw `#4d5928` and still water must draw `#304965`, whatever the
 * ramp around them looks like.
 */
export interface TerrainRamp {
  readonly indices: readonly number[];
  readonly colours: readonly PaletteColour[];
  readonly centre: number;
}

/**
 * Ground, dark to light, hue-locked to the measured ground green with **index
 * 143 at the centre**. Eleven entries rather than the whole cone: the palette
 * has a real gap between luminance 61 (index 99) and 80 (index 112), so
 * stepping past index 99 is a cliff, and the run is cut where the eye would
 * start seeing bands instead of light.
 *
 * The bright half — 113, 114, 115, 116, 119 — *is* the ramp the original paints
 * its own hills with (gi-format.md §10). New relief therefore lands in exactly
 * the colours the game's existing landforms are drawn in.
 */
export const GROUND_RELIEF_RAMP: TerrainRamp = {
  indices: [94, 96, 98, 99, 112, 143, 113, 114, 115, 116, 119],
  colours: [0x1c2c10, 0x243414, 0x283c1c, 0x2c4520, 0x595130, 0x4d5928, 0x5d5530, 0x615934, 0x655d38, 0x6d693c, 0x7d7149],
  centre: 5,
};

/** `BARON.PAL` 1-7 — the game's own water run, with the measured river/sea colour (4) at the centre. */
export const WATER_RAMP: TerrainRamp = {
  indices: [1, 2, 3, 4, 5, 6, 7],
  colours: [0x203449, 0x243c51, 0x284559, 0x304965, 0x34516d, 0x385d75, 0x416582],
  centre: 3,
};

/** `shadeLevelFromCosine`'s range — `clamp(floor(16 cos), -16, 15) + 16` (shading-and-paint.md §2). */
export const SHADE_LEVELS = 32;

/**
 * How much of the ramp a terrain face's slope is allowed to sweep — the
 * terrain's `shadeFront`, in the original's own terms.
 *
 * This is a chosen number, and it is the only one in this module the data does
 * not supply, so it is listed here in the manner enhanced-models.md §4.3 asks
 * for. What makes it a *principled* choice rather than a fudge is that the
 * format already has this parameter: world-terrain.md §7.2 established that
 * `shadeFront` is a per-face **lighting range** the engine walks at runtime,
 * not a fixed offset — every face gets to say how much of its ramp its own
 * geometry spans. Terrain faces in the shipped data never say, because the
 * shipped terrain is flat and their `halfWidth` is 0. Relief has to pick one.
 *
 * Why it cannot be 1. The engine's sun is horizontal, on world `-Z`
 * (`three/ramp-shade.ts`). An aeroplane's normals cover the whole sphere and so
 * sweep the ramp as it banks; a landscape's do not. Measured over Verdun's
 * derived surface, **91% of the ground lies under 2° of slope and none of it
 * exceeds 4°** — which is honest, gentle Meuse country and exactly what it
 * should be. But `floor(16 cos θ)` has a resolution of 3.6° near the horizon,
 * so at gain 1 the entire landscape quantises to a single palette entry and the
 * relief is invisible. That is the failure this constant prevents.
 *
 * At 9, a 1° slope moves one ramp step, 2° moves two and 4° moves three, so the
 * range the terrain actually occupies is the range the ramp spends — and level
 * ground is still exactly `#4d5928`.
 */
export const TERRAIN_SHADE_GAIN = 9;

/**
 * The original's own quantiser (shading-and-paint.md §2), with the gain above
 * applied first. Returns `0 .. SHADE_LEVELS - 1`.
 */
export function terrainShadeLevel(cosine: number): number {
  const scaled = Math.max(-1, Math.min(1, cosine * TERRAIN_SHADE_GAIN));
  return Math.max(-16, Math.min(15, Math.floor(16 * scaled))) + 16;
}

/**
 * Walks a ramp by shade level. Level 16 — an unlit face, which for a horizontal
 * sun is level ground — returns `ramp.centre` exactly, so the measured colour
 * survives the walk untouched. Either side, the level's distance from 16 is
 * scaled onto the shorter of the ramp's two arms, so the walk is symmetric and
 * never runs off an end.
 */
export function rampColourAt(ramp: TerrainRamp, level: number): PaletteColour {
  return ramp.colours[rampIndexAt(ramp, level)]!;
}

/**
 * The *position* the walk lands on rather than the colour there.
 *
 * Kept separate because a terrain face has to be able to move along its ramp
 * after it is built: the hour of the day dims the ground by walking the same
 * run down a few steps (`flight/time-of-day.ts`'s `groundRampShiftFor`), and
 * storing the index per vertex is what lets that be a colour rewrite instead of
 * a geometry rebuild.
 */
export function rampIndexAt(ramp: TerrainRamp, level: number): number {
  const arm = Math.min(ramp.centre, ramp.colours.length - 1 - ramp.centre);
  const at = ramp.centre + Math.round(((level - 16) * arm) / 16);
  return Math.max(0, Math.min(ramp.colours.length - 1, at));
}

/** A ramp position shifted and clamped — the hour's dimming, applied to one face. */
export function shiftRampIndex(ramp: TerrainRamp, index: number, shift: number): number {
  return Math.max(0, Math.min(ramp.colours.length - 1, index + shift));
}

/**
 * A terrain face's colour: its normal dotted with the engine's horizontal sun,
 * quantised, walked along `ramp`. No lighting, no interpolation — the result is
 * always one of `ramp`'s own entries.
 */
export function shadeTerrainFace(ramp: TerrainRamp, normalX: number, normalY: number, normalZ: number): PaletteColour {
  return ramp.colours[shadeTerrainFaceIndex(ramp, normalX, normalY, normalZ)]!;
}

/** `shadeTerrainFace`, as a ramp position — see `rampIndexAt`. */
export function shadeTerrainFaceIndex(ramp: TerrainRamp, normalX: number, normalY: number, normalZ: number): number {
  const length = Math.hypot(normalX, normalY, normalZ) || 1;
  // `SUN_DIRECTION_WORLD` is (0, 0, -1), so the dot product is just -nz.
  return rampIndexAt(ramp, terrainShadeLevel(-normalZ / length));
}

/**
 * `BARON.PAL` 64-79 — the road greys, a clean sixteen-step run. The three the
 * game actually paints roads with (67, 68, 70) sit inside it.
 */
export const ROAD_RAMP: TerrainRamp = {
  indices: [64, 65, 66, 67, 68, 69, 70, 71, 72],
  colours: [0x141414, 0x1c1c1c, 0x242424, 0x2c2c2c, 0x343434, 0x3c3c3c, 0x494949, 0x515151, 0x595959],
  centre: 4,
};

/**
 * `BARON.PAL` 120-127 — ploughed and bare earth, and a genuine run: 72, 72, 76,
 * 80, 88, 91, 109, 121 in luminance, one hue throughout. It matters more than
 * its size suggests, because index **124** is what the big field blocks are
 * painted with and they are most of what a pilot sees at low level.
 */
export const PLOUGHED_RAMP: TerrainRamp = {
  indices: [120, 122, 123, 124, 125, 126, 127],
  colours: [0x594534, 0x5d4938, 0x614d3c, 0x6d553c, 0x795538, 0x86694d, 0x927559],
  centre: 3,
};

/**
 * The ramps a *placed* terrain face is allowed to be walked down when the hour
 * dims the world.
 *
 * Deliberately not "every colour the terrain uses". §3.3's recovery found that
 * `BARON.PAL`'s terrain colours split in two: genuine **runs** (water 1-7,
 * roads 64-72, ploughed earth 120-127, and the ground/hill green family) and
 * the **landcover block** 128-151, which is two dozen unrelated agricultural
 * tones with no ordering at all. A run can be walked; an isolated tone cannot, and manufacturing a
 * darker neighbour for it would put colours on those polygons that the game
 * never does — which is the one thing this whole lane refuses to do.
 *
 * So a green field, a ploughed one, a river and a road all darken with the
 * hour; the multi-tone landcover patchwork does not. That is a real, visible half-measure and it is recorded
 * as such in docs/engineering/enhanced-terrain.md rather than papered over: the
 * other half needs somebody to *author* a dusk tone for each landcover entry,
 * exactly as the sky lane authored one for the ground plane.
 */
const DIMMABLE: readonly TerrainRamp[] = [GROUND_RELIEF_RAMP, WATER_RAMP, ROAD_RAMP, PLOUGHED_RAMP];


/**
 * The hour's luminance factors, taken from the sky lane's own ladder for the
 * ground plane: 143 -> 99 (dawn), 98 (dusk), 96 (night) is 0.736, 0.645, 0.558
 * of the noon luminance. Nothing new is decided here — the terrain simply
 * darkens by as much as the flat ground already did.
 */
export const HOUR_LUMINANCE_FACTORS: readonly number[] = [0.736, 0.645, 0.558];

/**
 * Darkened tones for the landcover block, which is the one part of the terrain
 * palette that is **not** a ramp — two dozen unrelated agricultural colours
 * with no ordering to walk (§3.3). They still have to darken, or a night
 * mission draws noon-bright fields under a black sky and a searchlight beam
 * reads as grey haze against them (AD-017 §8).
 *
 * So instead of a walk, a **palette snap**: multiply the tone by the hour's
 * factor above and take the nearest real `BARON.PAL` entry. That is what a 1990
 * artist did when they needed a darker version of a colour and had 256 of them
 * — the result is always a colour the game ships, and measured over all 23
 * entries the hue drift is 0.969-1.000 of a unit dot product, i.e. the tone
 * darkens without turning into a different colour.
 *
 * Three entries per row: dawn, dusk, night. Re-derived from the palette by
 * `test/terrain-palette.test.ts`, so a wrong number here fails rather than
 * quietly painting a field the wrong colour.
 */
export const LANDCOVER_NIGHT_LADDER: Readonly<Record<number, readonly [number, number, number]>> = {
  0x826538: [0x694528, 0x5d3c20, 0x51341c],
  0x96865d: [0x716541, 0x615934, 0x594534],
  0x79712c: [0x4d5928, 0x4d5928, 0x51341c],
  0x7d7145: [0x5d5530, 0x594534, 0x51341c],
  0x616d10: [0x4d5928, 0x493400, 0x493400],
  0x516534: [0x2c4520, 0x2c4520, 0x283c1c],
  0x556134: [0x2c4520, 0x2c4520, 0x283c1c],
  0x717134: [0x4d5928, 0x4d5928, 0x2c4520],
  0x756530: [0x595130, 0x51341c, 0x452c14],
  0x8e6941: [0x6d4534, 0x594534, 0x51341c],
  0x8a6d3c: [0x5d5530, 0x5d3c20, 0x51341c],
  0x866138: [0x694528, 0x5d3c20, 0x51341c],
  0x826930: [0x69511c, 0x5d3c20, 0x51341c],
  0x866934: [0x694528, 0x5d3c20, 0x51341c],
  0x927149: [0x6d553c, 0x594534, 0x5d3c20],
  0x926d41: [0x755130, 0x694528, 0x51341c],
  0xa27541: [0x755130, 0x694528, 0x5d3c20],
  0xa67124: [0x6d5120, 0x654910, 0x5d3c20],
  0xa27530: [0x6d5120, 0x69511c, 0x5d3c20],
  0xa26d14: [0x755100, 0x654910, 0x654504],
  0x926104: [0x694500, 0x654504, 0x553400],
  0x8e6110: [0x654910, 0x654504, 0x553400],
  0x92610c: [0x654504, 0x654504, 0x553400],
};

/**
 * One placed face's colour at this hour: walked down its own run where it is on
 * one, snapped through `LANDCOVER_NIGHT_LADDER` where it is not, and returned
 * unchanged when it is neither — or when the hour is noon.
 */
export function dimTerrainColour(colour: PaletteColour, shift: number): PaletteColour {
  if (shift === 0) return colour;
  for (const ramp of DIMMABLE) {
    const at = ramp.colours.indexOf(colour);
    if (at >= 0) return ramp.colours[shiftRampIndex(ramp, at, shift)]!;
  }
  // Shifts are -2/-3/-4 for dawn/dusk/night (`flight/time-of-day.ts`).
  const step = Math.min(2, Math.max(0, -shift - 2));
  return LANDCOVER_NIGHT_LADDER[colour]?.[step] ?? colour;
}

/** `0xRRGGBB` split into the 0..1 floats a `vertexColors` attribute wants. */
export function colourToRgb(colour: PaletteColour): [number, number, number] {
  return [((colour >> 16) & 0xff) / 255, ((colour >> 8) & 0xff) / 255, (colour & 0xff) / 255];
}
